import { useGT } from 'gt-react';
import type { QuickSchemaIssue } from './quickSchemaSave';

interface IssueToast {
  title: string;
  message: string;
  color: 'warning' | 'error';
}

/** The toast for each reason the New schema dialog refuses to save. */
export function useQuickSchemaIssueToasts(): Record<
  QuickSchemaIssue,
  IssueToast
> {
  const gt = useGT();
  return {
    missing_extension_target: {
      title: gt('Choose an entity', {
        context: 'validation toast quick schema extension entity',
      }),
      message: gt('Pick the entity this extension adds fields to.', {
        context: 'validation toast body quick schema extension entity',
      }),
      color: 'warning',
    },
    missing_name: {
      title: gt('Add a name', { context: 'validation toast quick schema' }),
      message: gt('Enter a short name for this schema.', {
        context: 'validation toast body quick schema',
      }),
      color: 'warning',
    },
    not_a_column: {
      title: gt('Some fields can’t be columns', {
        context: 'validation toast quick schema sql field type',
      }),
      message: gt(
        'Object and array fields can’t be stored as columns. Change their type or remove them.',
        { context: 'validation toast body quick schema sql field type' },
      ),
      color: 'warning',
    },
    invalid_default: {
      title: gt('Invalid default value', {
        context: 'toast title invalid schema default value',
      }),
      message: gt(
        'Enter a valid value for each enabled default, or turn off "Has default".',
        { context: 'toast body invalid schema default value' },
      ),
      color: 'error',
    },
    invalid_numeric: {
      title: gt('Invalid numeric precision/scale', {
        context: 'toast title invalid numeric precision scale',
      }),
      message: gt(
        'Enter a whole-number precision (1 or more) and a scale from 0 up to the precision for each numeric field.',
        { context: 'toast body invalid numeric precision scale' },
      ),
      color: 'error',
    },
    missing_referenced_entity: {
      title: gt('Choose an entity', {
        context: 'toast title when an entity schema field has no entity',
      }),
      message: gt('Each Entity field must name the record it points at.', {
        context: 'toast body when an entity schema field has no entity',
      }),
      color: 'error',
    },
    invalid_sql_default: {
      title: gt('Invalid default value', {
        context: 'toast title invalid sql schema default value',
      }),
      message: gt(
        'One or more default values do not fit their column type (e.g. too long, out of range, or wrong precision/scale).',
        { context: 'toast body invalid sql schema default value' },
      ),
      color: 'error',
    },
    no_fields: {
      title: gt('Add at least one field', {
        context: 'validation toast quick schema',
      }),
      message: gt(
        'Use "Add field" or optional AI suggestions, then try again.',
        { context: 'validation toast body quick schema' },
      ),
      color: 'warning',
    },
  };
}
